import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { catchError, combineLatest, debounceTime, distinctUntilChanged, finalize, from, map, Observable, of, startWith, switchMap, take } from 'rxjs';

import { Button, ButtonIcon, ButtonLabel } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { Tooltip } from 'primeng/tooltip';

import { TranslationModule } from '@core/translation/translation.module';
import { BcmSettingsFacade } from '@bcmServices/settings/bcm-settings-facade';
import { BcmSettings, BcmSettingsSectionName } from '@shared/models/bcm-settings';
import { CompanyApiService } from '@bcmApiServices/company.api-service';
import { PersonApiService } from '@bcmApiServices/person.api-service';
import { TenantRelationsApiService } from '@bcmApiServices/tenant-relations.api-service';
import { BcmService } from '@modules/bcm/bcm.service';
import { Season } from '@shared/models/seasons';
import { Salutation } from '@shared/models/salutation';
import { U2BEmailValidator } from '@shared/validators/core/email';
import { TenantRelationAssignment } from '@shared/models/tenant-relation-assignment';
import { U2bConfirmDialogComponent } from '@u2bUi/dialog/u2b-confirm-dialog.component';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';
import { U2bIndicatorCardComponent } from '@u2bUi/indicator-card/u2b-indicator-card.component';
import { U2bSegmentedToggleComponent, U2bSegmentedToggleOption } from '@u2bUi/segmented-toggle/u2b-segmented-toggle.component';

import {
    BookingV2PersonKind,
    BookingV2PersonOption,
    BookingV2RelationLineOverride,
    BookingV2RelationOption,
    BookingV2RelationProduct,
    BookingV2WinterStorageOption,
} from '../../dtos/booking-v2.dtos';
import { BookingV2DynamicPriceBreakdownComponent } from '../../components/dynamic-price-breakdown/booking-v2-dynamic-price-breakdown.component';
import { BookingV2Facade } from '../../services/booking-v2.facade';
import { BookingV2RelationPricingService } from '../../services/booking-v2-relation-pricing.service';
import { isUp2boatId, Up2boatIdSelectionService } from '@modules/bcm/@shared/services/up2boat-id-selection.service';
import { isBookingV2SearchTooShort } from '../../utils/booking-v2-search-query.util';
import { relationRuleRequirements, toCompanyOption, toPersonOption, toRelationProducts } from '../../utils/booking-v2-option.mappers';
import {
    relationProductAutoTitle,
    relationProductIsUnbillable,
    relationProductQuantity,
    relationProductTitle,
    relationProductUnitPrice,
} from '../../utils/booking-v2-relation-billing';
import { duplicateRelationIdsFromAssignments } from '../../utils/booking-v2-relation-duplicate.util';
import {
    bookingV2RelationLabelKey,
    bookingV2RelationProductsEmptyLabelKey,
} from '../../utils/booking-v2-relation-label.util';
import { formatMoney, formatQuantity, grossOf } from '../../utils/booking-v2-pricing';
import { withKey } from '../../utils/booking-v2-record';

type PersonMode = 'search' | 'new' | null;

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;
const NOTES_MAX_LENGTH = 1024;
// In-memory draft id for a quick-created person/org (not yet persisted).
const NEW_PERSON_DRAFT_ID = -1;

@Component({
    selector: 'booking-v2-person-step',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        Button,
        ButtonIcon,
        ButtonLabel,
        IconField,
        InputIcon,
        InputNumber,
        InputText,
        ProgressSpinner,
        Select,
        Textarea,
        Tooltip,
        TranslationModule,
        BookingV2DynamicPriceBreakdownComponent,
        U2bIconButtonComponent,
        U2bIndicatorCardComponent,
        U2bSegmentedToggleComponent,
    ],
    templateUrl: './person-step.component.html',
    host: { class: 'block' },
})
export class PersonStepComponent implements OnInit {

    private readonly facade = inject(BookingV2Facade);
    private readonly bcm = inject(BcmService);
    private readonly personApi = inject(PersonApiService);
    private readonly companyApi = inject(CompanyApiService);
    private readonly tenantRelationsApi = inject(TenantRelationsApiService);
    private readonly relationPricing = inject(BookingV2RelationPricingService);
    private readonly settingsFacade = inject(BcmSettingsFacade);
    private readonly overlay = inject(Overlay);
    private readonly destroyRef = inject(DestroyRef);
    private readonly up2boatIds = inject(Up2boatIdSelectionService);

    // Reactive so the default-relation effect re-runs once settings arrive on a
    // cold cache (same load pattern as the electric-meters tab).
    private readonly settingsSig = toSignal(
        this.settingsFacade.loadSettings().pipe(catchError(() => of<BcmSettings | undefined>(undefined))),
        { initialValue: undefined as BcmSettings | undefined },
    );

    constructor() {
        // Preselect the tenant's configured default relation (Settings →
        // default-units `dayGuestBoater`), mirroring the legacy dialog. One-shot,
        // create flow only; waits for a person (the relation card is person-gated),
        // the relations list and settings, and never overrides an existing choice.
        effect(() => {
            const person = this.facade.selectedPerson();
            const relations = this.relations();
            const settings = this.settingsSig();
            if (this.facade.relationDefaultApplied() || this.facade.isEditMode()) {
                return;
            }
            if (!person || relations.length === 0 || !settings) {
                return;
            }
            this.facade.relationDefaultApplied.set(true);
            if (this.facade.selectedRelation() !== null) {
                return;
            }
            const defaultId = settings[BcmSettingsSectionName.DefaultUnits]?.dayGuestBoater ?? null;
            const option = defaultId != null ? relations.find(r => r.id === defaultId) : undefined;
            if (option) {
                this.applyRelationSelection(option);
            }
        });

        // A rule naming other Beziehungen than the booking's: preselect the one the guest
        // actually holds, if exactly one — never a relation the guest does not have. Once
        // per relation + product, so a cleared choice stays cleared. (#7044)
        effect(() => {
            const relationId = this.selectedRelation()?.id;
            const activeIds = new Set(this.activeRelationAssignments()
                .map(a => a.tenantRelation?.id)
                .filter((id): id is number => id != null));
            const picked = this.facade.relationPricingRelationIds();
            if (relationId == null || !activeIds.size) {
                return;
            }
            for (const p of this.selectedRelationProducts()) {
                const key = `${relationId}:${p.productId}`;
                const preselected = this.facade.relationPricingPreselected;
                if (!this.pricingRelationChoices().has(p.productId) || picked[p.productId] != null || preselected.has(key)) {
                    continue;
                }
                preselected.add(key);
                const candidates = (p.tenantRelationCriteriaIds ?? []).filter(id => activeIds.has(id));
                if (candidates.length === 1) {
                    this.relationPricing.setPricingRelation(p.productId, candidates[0]);
                }
            }
        });
    }

    private duplicateConfirmOverlayRef: OverlayRef | null = null;

    // Season picker + warnings for the RELATION context only (shown in the relation
    // card). The services tab shows its own service-context variants. Both pickers
    // bind the same global selectedSeason.
    readonly showSeasonPicker = this.relationPricing.relationShowSeasonPicker;
    readonly seasons = this.relationPricing.seasons;
    readonly selectedSeason = this.facade.selectedSeason;
    // Required-but-unset (combined relation+services) → the picker shows `*` and Save
    // is blocked (commit service).
    readonly seasonMissing = this.relationPricing.seasonMissing;

    // Lagerplatz picker for the RELATION context (same pattern as the season). Not a
    // Save-gate — just `*` + hint while a Lagerplatz rule applies but none is chosen.
    readonly showWinterStoragePicker = this.relationPricing.relationShowWinterStoragePicker;
    readonly winterStorages = this.relationPricing.winterStorages;
    readonly selectedWinterStorage = this.facade.selectedWinterStorage;
    readonly winterStorageMissing = this.relationPricing.winterStorageMissing;

    // Unsatisfiable RELATION rule entities (missing berth) → hint.
    readonly priceWarnings = this.relationPricing.relationPriceWarnings;

    readonly notesMaxLength = NOTES_MAX_LENGTH;

    readonly personTypeOptions: U2bSegmentedToggleOption<BookingV2PersonKind>[] = [
        { value: 'person', icon: 'person', labelKey: 'bookingV2.person.type.person' },
        { value: 'company', icon: 'business', labelKey: 'bookingV2.person.type.company' },
    ];

    readonly selectedPerson = this.facade.selectedPerson;
    readonly selectedRelation = this.facade.selectedRelation;
    readonly bookingNotes = this.facade.bookingNotes;

    // Which panel is open (`null` = none). Tiles toggle this.
    readonly personMode = signal<PersonMode>(null);

    // ── Existing person/company search ──
    readonly personQuery = signal<string>('');
    readonly loadingPersonSearch = signal<boolean>(false);
    readonly importingPersonFromQr = signal<boolean>(false);

    readonly personSearchResult = toSignal(
        toObservable(this.personQuery).pipe(
            debounceTime(SEARCH_DEBOUNCE_MS),
            map(q => q.trim()),
            distinctUntilChanged(),
            switchMap(q => {
                if (isUp2boatId(q)) {
                    this.loadingPersonSearch.set(true);
                    return from(this.up2boatIds.resolvePerson(q)).pipe(
                        switchMap(resolution => {
                            // Keine bekannte Up2Boat-ID (z. B. Nachname "Becker"): normale Suche.
                            if (!resolution.found) {
                                return this.searchPersonOptions(q);
                            }
                            const person = resolution.value;
                            if (person?.id) {
                                this.selectExistingPerson(toPersonOption(person));
                                this.personQuery.set('');
                            }
                            return of<readonly BookingV2PersonOption[]>([]);
                        }),
                        catchError(() => of<readonly BookingV2PersonOption[]>([])),
                        finalize(() => this.loadingPersonSearch.set(false)),
                    );
                }
                if (q.length < MIN_SEARCH_LENGTH) {
                    this.loadingPersonSearch.set(false);
                    return of<readonly BookingV2PersonOption[]>([]);
                }
                this.loadingPersonSearch.set(true);
                return this.searchPersonOptions(q);
            }),
        ),
        { initialValue: [] as readonly BookingV2PersonOption[] },
    );

    private searchPersonOptions(q: string): Observable<readonly BookingV2PersonOption[]> {
        const params = new HttpParams()
            .set('filter', q)
            .set('size', '30');
        const persons$ = this.personApi.search(params).pipe(
            map(list => list.map(toPersonOption)),
            startWith<readonly BookingV2PersonOption[]>([]),
            catchError(() => of<readonly BookingV2PersonOption[]>([])),
        );
        const companies$ = this.companyApi.index(params).pipe(
            map(page => (page?.results ?? []).map(toCompanyOption)),
            startWith<readonly BookingV2PersonOption[]>([]),
            catchError(() => of<readonly BookingV2PersonOption[]>([])),
        );
        return combineLatest([persons$, companies$]).pipe(
            map(([persons, companies]): readonly BookingV2PersonOption[] => [...persons, ...companies]),
            finalize(() => this.loadingPersonSearch.set(false)),
        );
    }

    readonly personQueryEmpty = computed(() => isBookingV2SearchTooShort(this.personQuery()));

    readonly personSearchHasResults = computed(() => this.personSearchResult().length > 0);

    readonly personShowNoResults = computed(() =>
        !this.loadingPersonSearch()
        && !this.personQueryEmpty()
        && !this.personSearchHasResults(),
    );

    // ── Quick-create new person/company ──
    readonly newPersonType = signal<BookingV2PersonKind>('person');
    readonly newFirstName = signal<string>('');
    readonly newLastName = signal<string>('');
    // Salutation (person only). Required on quick-create (#7243); backend NOT NULL.
    readonly newSalutation = signal<Salutation | null>(null);
    // Mutable array (a `p-select [options]` rejects a readonly array).
    readonly salutationOptions: { value: Salutation; labelKey: string }[] = [
        { value: Salutation.Male, labelKey: 'bookingV2.person.salutationOptions.male' },
        { value: Salutation.Female, labelKey: 'bookingV2.person.salutationOptions.female' },
        { value: Salutation.Divers, labelKey: 'bookingV2.person.salutationOptions.diverse' },
        { value: Salutation.None, labelKey: 'bookingV2.person.salutationOptions.none' },
    ];
    /** Quick-create: „Keine“ is not a valid required salutation (#7243). */
    readonly newPersonSalutationOptions = this.salutationOptions.filter(o => o.value !== Salutation.None);
    readonly newCompanyName = signal<string>('');
    readonly newContactPerson = signal<string>('');
    readonly newAddress = signal<string>('');
    readonly newPhone = signal<string>('');
    readonly newMail = signal<string>('');

    readonly newPersonValid = computed(() => {
        const mailOk = isQuickCreateEmailValid(this.newMail(), true);
        if (!mailOk) {
            return false;
        }
        if (this.newPersonType() === 'company') {
            return this.newCompanyName().trim().length > 0;
        }
        return this.newSalutation() != null
            && this.newLastName().trim().length > 0;
    });

    /** Step header + quick-create panel follow Person/Organisation toggle (#7243). */
    private readonly quickCreateTypeKind = computed<BookingV2PersonKind | null>(() =>
        this.personMode() === 'new' && !this.selectedPerson() ? this.newPersonType() : null,
    );

    readonly stepTitleKey = computed(() => {
        const kind = this.quickCreateTypeKind();
        if (kind === 'company') {
            return 'bookingV2.person.titleCompany';
        }
        if (kind === 'person') {
            return 'bookingV2.person.titlePerson';
        }
        return 'bookingV2.person.title';
    });

    readonly stepSubtitleKey = computed(() => {
        const kind = this.quickCreateTypeKind();
        if (kind === 'company') {
            return 'bookingV2.person.subtitleCompany';
        }
        if (kind === 'person') {
            return 'bookingV2.person.subtitlePerson';
        }
        return 'bookingV2.person.subtitle';
    });

    readonly newFormTitleKey = computed(() =>
        this.newPersonType() === 'company'
            ? 'bookingV2.person.new.titleCompany'
            : 'bookingV2.person.new.titlePerson',
    );

    readonly newFormSaveLabelKey = computed(() =>
        this.newPersonType() === 'company'
            ? 'bookingV2.person.new.saveCompany'
            : 'bookingV2.person.new.savePerson',
    );

    // ── Beziehung (tenant relations) ──
    // Loaded once by the pricing service, which also needs it for the pricing-relation pickers.
    readonly relations = this.relationPricing.relationOptions;

    // Leistungen attached to the selected Beziehung — read-only preview.
    // Products travel on the selected option (create) or are enriched on the
    // facade by the dialog's edit-load, so reading them straight off works in both.
    readonly selectedRelationProducts = computed<readonly BookingV2RelationProduct[]>(
        () => this.selectedRelation()?.products ?? [],
    );

    // Ohne Regeltreffer wird das Produkt nicht abgerechnet. Die Zeile bleibt trotzdem stehen, nur
    // ohne Preis und mit Hinweis – wie die Box "Produkte gefunden" im alten Dialog. Herausgefiltert
    // behauptete die Karte "keine Leistungen zugeordnet", sobald alle Regeln danebenlagen. (#7017)
    isNotBilled(product: BookingV2RelationProduct): boolean {
        return relationProductIsUnbillable(product);
    }

    // ── Bezeichnung / Preis der Beziehungs-Leistungen (#7044) ──
    // Named after the matched rule like on the Bierdeckel; a hand edit overrides it.

    relationLineTitle(product: BookingV2RelationProduct): string {
        return relationProductTitle(product, this.facade.effectiveRelationLineOverrides()[product.productId]);
    }

    relationLineUnitPrice(product: BookingV2RelationProduct): number {
        return relationProductUnitPrice(product, this.facade.effectiveRelationLineOverrides()[product.productId]);
    }

    hasRelationLineOverride(product: BookingV2RelationProduct): boolean {
        return this.facade.effectiveRelationLineOverrides()[product.productId] != null;
    }

    readonly canEditRelationLines = this.facade.relationLinesBilledAsPositions;

    readonly editingRelationProductId = signal<number | null>(null);
    readonly editRelationTitle = signal<string>('');
    readonly editRelationPrice = signal<number | null>(null);

    openRelationLineEditor(product: BookingV2RelationProduct): void {
        this.editingRelationProductId.set(product.productId);
        this.editRelationTitle.set(this.relationLineTitle(product));
        this.editRelationPrice.set(this.relationLineUnitPrice(product));
    }

    closeRelationLineEditor(): void {
        this.editingRelationProductId.set(null);
    }

    // Stores only what differs from the rule, so the rest keeps following it.
    applyRelationLineEdit(product: BookingV2RelationProduct): void {
        const title = this.editRelationTitle().trim();
        const price = this.editRelationPrice();
        const override: BookingV2RelationLineOverride = {};
        if (title && title !== relationProductAutoTitle(product)) {
            override.title = title;
        }
        if (price != null && Math.round(price * 100) !== Math.round(product.price * 100)) {
            override.price = price;
        }
        this.setRelationLineOverride(product.productId, override.title !== undefined || override.price !== undefined ? override : null);
        this.closeRelationLineEditor();
    }

    resetRelationLine(product: BookingV2RelationProduct): void {
        this.setRelationLineOverride(product.productId, null);
        this.closeRelationLineEditor();
    }

    private setRelationLineOverride(productId: number, override: BookingV2RelationLineOverride | null): void {
        this.facade.relationLineOverrides.update(current => withKey(current, productId, override));
    }

    // ── Beziehung für die Preisregel (#7044) ──
    // The relations a product's rule names, for the products whose rule the booking's own
    // relation cannot satisfy (the Bierdeckel's "Dynamischer Preis" asks the same).
    readonly pricingRelationChoices = computed<ReadonlyMap<number, BookingV2RelationOption[]>>(() => {
        const choices = new Map<number, BookingV2RelationOption[]>();
        if (!this.canEditRelationLines()) {
            return choices;
        }
        for (const p of this.selectedRelationProducts()) {
            if (this.relationPricing.needsPricingRelation(p)) {
                choices.set(p.productId, this.relationPricing.pricingRelationOptions(p.tenantRelationCriteriaIds));
            }
        }
        return choices;
    });

    pickedPricingRelationId(product: BookingV2RelationProduct): number | null {
        return this.facade.relationPricingRelationIds()[product.productId] ?? null;
    }

    setPricingRelation(product: BookingV2RelationProduct, relationId: number | null): void {
        this.relationPricing.setPricingRelation(product.productId, relationId);
    }

    // ── Aktive Beziehungen der gewählten Person (Duplikat-Hinweis #6839) ──
    // Aktive Beziehungen der gewählten Person/Organisation, nachgeladen aus der
    // vollständigen Entität (wie der Reiter Personen→Beziehung). Reagiert auf jeden
    // Wechsel von selectedPerson (manuelle Auswahl, Boots-Eigentümer-Default,
    // Edit-Load); Entwürfe (origin 'new', id ≤ 0) haben noch keine Beziehungen.
    private readonly activeRelationAssignments = toSignal(
        toObservable(this.selectedPerson).pipe(
            distinctUntilChanged((a, b) =>
                a?.id === b?.id && a?.kind === b?.kind && a?.origin === b?.origin),
            switchMap(person => {
                if (!person || person.origin === 'new' || person.id <= 0) {
                    return of<readonly TenantRelationAssignment[]>([]);
                }
                const full$ = person.kind === 'company'
                    ? this.companyApi.getOne(person.id).pipe(map(c => c.activeTenantRelationAssignments))
                    : this.personApi.getOne(person.id).pipe(map(p => p.activeTenantRelationAssignments));
                return full$.pipe(catchError(() => of<readonly TenantRelationAssignment[]>([])));
            }),
        ),
        { initialValue: [] as readonly TenantRelationAssignment[] },
    );

    // Beziehungstypen, die der Gast bereits aktiv hat — wählbar mit Bestätigung (#6839).
    // Pro Typ, unabhängig vom Boot. Im Edit-Modus zählt die gerade bearbeitete Zuweisung
    // nicht als ihr eigenes Duplikat.
    readonly duplicateRelationIds = computed<ReadonlySet<number>>(() =>
        duplicateRelationIdsFromAssignments(
            this.activeRelationAssignments(),
            this.selectedRelation()?.assignmentId ?? null,
        ),
    );

    grossOf = grossOf;
    fmt = formatMoney;

    fmtQty = formatQuantity;

    readonly currentLocale = BcmService.currentLocale;

    /**
     * Anzahl, mit der das Beziehungsprodukt später abgerechnet wird – über denselben Helfer wie
     * die gebuchte Position, damit Karte und Beleg nicht auseinanderlaufen. (#7017)
     */
    relationQuantity(product: BookingV2RelationProduct): number {
        const { from, to } = this.facade.dateRange();

        return relationProductQuantity(
            product,
            from,
            to,
            this.selectedRelation()?.payableOption ?? null,
            this.facade.defaultTimeUnit(),
        );
    }

    relationKey(suffix: string): string {
        const tenant = this.bcm.tenant;
        return bookingV2RelationLabelKey(suffix, tenant.registerType, tenant.productType);
    }

    relationProductsEmptyKey(): string {
        const tenant = this.bcm.tenant;
        return bookingV2RelationProductsEmptyLabelKey(tenant.registerType, tenant.productType);
    }

    readonly relationQuery = signal<string>('');

    readonly filteredRelations = computed<readonly BookingV2RelationOption[]>(() => {
        const q = this.relationQuery().trim().toLowerCase();
        const all = this.relations();
        if (!q) {
            return all;
        }
        return all.filter(r =>
            r.name.toLowerCase().includes(q)
            || (r.note?.toLowerCase().includes(q) ?? false),
        );
    });

    ngOnInit(): void {
        this.applyBoatOwnerDefault();
        this.destroyRef.onDestroy(() => this.disposeDuplicateConfirmOverlay());
    }

    // Default the booking's person to the selected boat's owner. Runs once per
    // dialog session (guarded by the facade) so it does not re-fire after the
    // user removes the prefilled owner. The owner identity rides along on the
    // boat option; full contact details are loaded lazily (the boat graph only
    // carries id + name).
    private applyBoatOwnerDefault(): void {
        if (this.facade.ownerDefaultApplied()) {
            return;
        }
        if (this.facade.selectedPerson() !== null) {
            return;
        }
        const boat = this.facade.selectedBoat();
        const ownerId = boat?.ownerId ?? null;
        const ownerKind = boat?.ownerKind ?? null;
        if (ownerId == null || ownerKind == null) {
            // No boat owner to take over yet — leave the one-shot unconsumed so
            // the prefill still fires once an owner-bearing boat is picked and
            // the user returns to this step. (Burning the guard up-front here
            // suppressed the prefill whenever the person step was opened before
            // such a boat had been selected — e.g. berth-only first, then boat.)
            return;
        }
        // Consume the one-shot only now that we actually prefill, so a later
        // manual removal of the owner is respected and does not re-fire.
        this.facade.ownerDefaultApplied.set(true);
        this.facade.selectedPerson.set({
            id: ownerId,
            kind: ownerKind,
            displayName: boat?.ownerLabel ?? '',
            contactPerson: null,
            address: null,
            phone: null,
            mail: null,
            origin: 'existing',
        });
        this.loadOwnerDetails(ownerId, ownerKind);
    }

    private loadOwnerDetails(id: number, kind: BookingV2PersonKind): void {
        const details$ = kind === 'company'
            ? this.companyApi.getOne(id).pipe(map(toCompanyOption))
            : this.personApi.getOne(id).pipe(map(toPersonOption));
        details$.pipe(
            catchError(() => of<BookingV2PersonOption | null>(null)),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(full => {
            if (!full) {
                return;
            }
            // Only fill in details if the prefilled owner is still selected
            // (the user may have swapped or removed the person meanwhile).
            const current = this.facade.selectedPerson();
            if (current?.origin === 'existing' && current.id === id && current.kind === kind) {
                this.facade.selectedPerson.set(full);
            }
        });
    }

    selectPersonMode(mode: Exclude<PersonMode, null>): void {
        this.personMode.set(this.personMode() === mode ? null : mode);
    }

    closePersonPanel(): void {
        this.personMode.set(null);
    }

    onPersonQueryChange(value: string): void {
        this.personQuery.set(value);
    }

    async scanPersonFromQr(): Promise<void> {
        if (this.importingPersonFromQr()) {
            return;
        }
        this.importingPersonFromQr.set(true);
        try {
            const person = await this.up2boatIds.resolvePersonFromQr();
            if (person?.id) {
                this.selectExistingPerson(toPersonOption(person));
                this.personQuery.set('');
            }
        } finally {
            this.importingPersonFromQr.set(false);
        }
    }

    selectExistingPerson(option: BookingV2PersonOption): void {
        this.facade.selectedPerson.set(option);
        this.personMode.set(null);
        this.personQuery.set('');
    }

    saveNewPerson(): void {
        if (!this.newPersonValid()) {
            return;
        }
        // Builds an in-memory draft (id -1, origin 'new'); the person/company is
        // persisted by the backend at booking commit (nested create), driven by
        // the `draft` fields below.
        const isCompany = this.newPersonType() === 'company';
        const firstName = this.newFirstName().trim();
        const lastName = this.newLastName().trim();
        const companyName = this.newCompanyName().trim();
        const contactPerson = this.newContactPerson().trim() || null;
        const address = this.newAddress().trim() || null;
        const phone = this.newPhone().trim() || null;
        const mail = this.newMail().trim() || null;
        const option: BookingV2PersonOption = {
            id: NEW_PERSON_DRAFT_ID,
            kind: isCompany ? 'company' : 'person',
            displayName: isCompany
                ? companyName
                : `${firstName} ${lastName}`.trim(),
            contactPerson: isCompany ? contactPerson : null,
            address,
            phone,
            mail,
            origin: 'new',
            draft: {
                firstName: isCompany ? null : firstName,
                lastName: isCompany ? null : lastName,
                salutation: isCompany ? null : (this.newSalutation() as Salutation),
                companyName: isCompany ? companyName : null,
                contactPerson: isCompany ? contactPerson : null,
                address,
                phone,
                mail,
            },
        };
        this.facade.selectedPerson.set(option);
        this.personMode.set(null);
        this.resetNewPersonForm();
    }

    removePerson(): void {
        this.facade.selectedPerson.set(null);
        // Relation + notes belong to the person — clear them too.
        this.facade.selectedRelation.set(null);
        this.facade.clearRelationLineInputs();
        this.facade.bookingNotes.set('');
        this.personMode.set(null);
    }

    onRelationQueryChange(value: string): void {
        this.relationQuery.set(value);
    }

    isRelationDuplicate(relation: BookingV2RelationOption): boolean {
        return this.duplicateRelationIds().has(relation.id);
    }

    selectRelation(relation: BookingV2RelationOption): void {
        if (this.isRelationDuplicate(relation)) {
            this.openDuplicateRelationConfirm(relation);
            return;
        }
        this.applyRelationSelection(relation);
    }

    private applyRelationSelection(relation: BookingV2RelationOption): void {
        this.facade.selectedRelation.set(relation);
        this.facade.clearRelationLineInputs();
        this.relationQuery.set('');

        this.tenantRelationsApi.getOne(relation.id).pipe(take(1)).subscribe({
            next: full => {
                // The user may have picked another relation while this was in flight.
                if (this.facade.selectedRelation()?.id !== relation.id) {
                    return;
                }
                this.facade.selectedRelation.set({
                    ...relation,
                    products: toRelationProducts(full),
                    payableOption: full.payableOption ?? null,
                    // Without it the season/Lagerplatz pickers never showed on create
                    // (lost in the 6622 lazy-load). (#7044)
                    requiredEntities: relationRuleRequirements(full),
                });
            },
            error: () => { /* product preview is optional */ },
        });
    }

    private openDuplicateRelationConfirm(relation: BookingV2RelationOption): void {
        if (this.duplicateConfirmOverlayRef) {
            return;
        }
        const overlayRef = this.overlay.create({
            hasBackdrop: false,
            scrollStrategy: this.overlay.scrollStrategies.block(),
            positionStrategy: this.overlay.position().global(),
        });
        const compRef = overlayRef.attach(new ComponentPortal(U2bConfirmDialogComponent));
        compRef.setInput('severity', 'warn');
        compRef.setInput('icon', 'warning');
        compRef.setInput('titleKey', this.relationKey('duplicateConfirm.title'));
        compRef.setInput('messageKey', this.relationKey('duplicateConfirm.message'));
        compRef.setInput('confirmKey', 'common.confirmAction');
        compRef.setInput('cancelKey', 'common.cancel');
        compRef.instance.confirmed.subscribe(() => {
            this.applyRelationSelection(relation);
            this.disposeDuplicateConfirmOverlay();
        });
        compRef.instance.cancelled.subscribe(() => this.disposeDuplicateConfirmOverlay());
        this.duplicateConfirmOverlayRef = overlayRef;
    }

    private disposeDuplicateConfirmOverlay(): void {
        this.duplicateConfirmOverlayRef?.dispose();
        this.duplicateConfirmOverlayRef = null;
    }

    setSeason(season: Season | null): void {
        this.relationPricing.setSeason(season);
    }

    setWinterStorage(option: BookingV2WinterStorageOption | null): void {
        this.relationPricing.setWinterStorage(option);
    }

    // Clear the primary relation — the picker reappears (it shows whenever no
    // relation is selected). Mirrors the trash-to-reselect flow of boat/berth.
    clearSelectedRelation(): void {
        this.facade.selectedRelation.set(null);
        this.facade.clearRelationLineInputs();
        this.relationQuery.set('');
    }

    isRelationSelected(relation: BookingV2RelationOption): boolean {
        return this.selectedRelation()?.id === relation.id;
    }

    onNotesChange(value: string): void {
        this.facade.bookingNotes.set((value ?? '').slice(0, NOTES_MAX_LENGTH));
    }

    private resetNewPersonForm(): void {
        this.newFirstName.set('');
        this.newLastName.set('');
        this.newSalutation.set(null);
        this.newCompanyName.set('');
        this.newContactPerson.set('');
        this.newAddress.set('');
        this.newPhone.set('');
        this.newMail.set('');
    }
}

function isQuickCreateEmailValid(raw: string, optional: boolean): boolean {
    const mail = raw.trim();
    if (!mail) {
        return optional;
    }
    return new U2BEmailValidator().isValidMail(mail);
}

